import React, { useCallback, useEffect, useState } from "react";
import { fetchUsageCosts, fetchUsageCostsReconcile, fetchMorphologyQuality } from "../api";

const fmt = (n) => `$${Number(n || 0).toFixed(Number(n) < 1 ? 4 : 2)}`;

function ReconcileRow({ label, info }) {
  return (
    <div className="flex items-center justify-between px-3 py-2 text-sm border-t border-slate-100">
      <span className="text-slate-700">{label}</span>
      {!info.configured ? (
        <span className="text-xs text-slate-400">not configured</span>
      ) : info.actual == null ? (
        <span className="text-xs text-amber-600">unavailable</span>
      ) : (
        <span className="text-slate-500">
          <span title="recorded">{fmt(info.recorded)}</span>
          {" → "}
          <span className="font-medium text-slate-800" title="actual billed">{fmt(info.actual)}</span>
          <span className={`ml-1 text-xs ${info.delta > 0 ? "text-rose-600" : "text-emerald-600"}`}>
            ({info.delta > 0 ? "+" : ""}{fmt(info.delta)})
          </span>
        </span>
      )}
    </div>
  );
}

// Friendly display names for cost feature keys — unmapped keys render as-is.
// Keep the Iʿrāb buckets distinct so free-tier spend is visible at a glance.
const FEATURE_LABELS = {
  word_morphology_free: "Iʿrāb tool — FREE TIER",
  word_morphology_public: "Iʿrāb tool — subscribers",
  word_morphology_prewarm: "Iʿrāb — Quran prewarm",
  word_morphology: "Word structure (admin)",
  verb_conjugator_free: "Conjugator — FREE TIER",
  verb_conjugator_public: "Conjugator — subscribers",
  ocr: "PDF reader OCR — subscribers",
  ocr_free: "PDF reader OCR — FREE TIER",
  sentence_translate: "PDF reader translate — subscribers",
  sentence_translate_free: "PDF reader translate — FREE TIER",
  word_analyze: "Word gloss — subscribers",
  word_analyze_free: "Word gloss — FREE TIER",
};

const pct = (v) => (v == null ? "—" : `${v}%`);

const QUALITY_SOURCE_LABELS = {
  refine_public: "Refine — free tier",
  refine_subscriber: "Refine — subscribers",
  prewarm: "Quran prewarm",
};

// Local-engine-vs-AI agreement telemetry (MorphologyComparison rows): the
// live quality meter for the Iʿrāb tool's instant answers, and the Track-2
// readiness gate. Shares the Costs tab's day-range selector.
function IrabQualityBlock({ days }) {
  const [quality, setQuality] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
    fetchMorphologyQuality(days)
      .then(setQuality)
      .catch(() => setFailed(true));
  }, [days]);

  if (failed) return null;
  if (!quality) return null;

  const sources = Object.entries(quality.by_source || {}).filter(([, r]) => r.total > 0);
  return (
    <div className="pt-2">
      <h4 className="text-sm font-semibold text-slate-700 mb-1.5">
        Iʿrāb quality — local engine vs AI
      </h4>
      {quality.overall.total === 0 ? (
        <p className="text-xs text-slate-400">
          No comparisons recorded yet — rows appear as AI refinement overwrites
          local-engine answers.
        </p>
      ) : (
        <>
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-1.5 text-sm bg-white">
              <span className="text-slate-700 font-medium">
                Overall ({quality.overall.total} word{quality.overall.total === 1 ? "" : "s"})
              </span>
              <span className="text-slate-500">
                case {pct(quality.overall.status_agree_pct)} · role {pct(quality.overall.role_agree_pct)}
              </span>
            </div>
            {sources.map(([source, r], i) => (
              <div key={source} className={`flex items-center justify-between px-3 py-1.5 text-sm ${i % 2 ? "bg-white" : "bg-slate-50"}`}>
                <span className="text-slate-700">{QUALITY_SOURCE_LABELS[source] || source} · {r.total}</span>
                <span className="text-slate-500">
                  case {pct(r.status_agree_pct)} · role {pct(r.role_agree_pct)}
                </span>
              </div>
            ))}
          </div>
          {quality.top_disagreements.length > 0 && (
            <div className="mt-2">
              <div className="text-xs text-slate-500 mb-1">Top role disagreements (local → AI)</div>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                {quality.top_disagreements.map((d, i) => (
                  <div key={i} className={`flex items-center justify-between px-3 py-1.5 text-sm ${i % 2 ? "bg-slate-50" : "bg-white"}`}>
                    <span className="text-slate-700" dir="rtl" lang="ar">
                      {d.local_role} ← {d.ai_role}
                    </span>
                    <span className="text-xs text-slate-400">{d.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      <p className="text-[11px] text-slate-400 mt-1.5">
        {quality.verified_count} verified gold example{quality.verified_count === 1 ? "" : "s"} (staff ✓ on word diagrams).
      </p>
    </div>
  );
}

function Table({ title, rows, keyField }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div>
      <h4 className="text-sm font-semibold text-slate-700 mb-1.5">{title}</h4>
      <div className="rounded-lg border border-slate-200 overflow-hidden">
        {rows.map((r, i) => (
          <div key={i} className={`flex items-center justify-between px-3 py-1.5 text-sm ${i % 2 ? "bg-slate-50" : "bg-white"}`}>
            <span className="text-slate-700">
              {(keyField === "feature" && FEATURE_LABELS[r[keyField]]) || r[keyField]}
              {r.is_staff && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-slate-400">admin</span>}
            </span>
            <span className="text-slate-500">
              <span className="font-medium text-slate-800">{fmt(r.cost)}</span>
              {r.count != null && <span className="text-xs text-slate-400"> · {r.count}</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminCostsPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [recon, setRecon] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);

  const reconcile = useCallback(() => {
    setReconLoading(true);
    fetchUsageCostsReconcile(days)
      .then(setRecon)
      .catch(() => setError("Reconcile failed."))
      .finally(() => setReconLoading(false));
  }, [days]);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    fetchUsageCosts(days)
      .then(setData)
      .catch(() => setError("Couldn't load usage costs."))
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-800">API Cost Rollup</h3>
          <p className="text-xs text-slate-500">Recorded AI / STT / TTS / OCR spend. Reconcile against provider billing.</p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
          className="px-2 py-1.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {error && <p className="text-sm text-rose-600">{error}</p>}
      {loading ? (
        <p className="text-sm text-slate-400 py-6 text-center">Loading…</p>
      ) : data ? (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl surface-glass px-4 py-3">
              <div className="text-xs text-dt-parchment-dim">Avg per user / month (non-admin)</div>
              <div className="text-xl font-bold text-dt-gold-bright">{fmt(data.avg_cost_per_user_month)}</div>
              <div className="text-[11px] text-dt-parchment-dim">{data.active_user_count} active user{data.active_user_count === 1 ? "" : "s"}</div>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
              <div className="text-xs text-slate-600">Total ({data.days} days)</div>
              <div className="text-xl font-bold text-slate-800">{fmt(data.total_cost)}</div>
              <div className="text-[11px] text-slate-400">
                {data.event_count} billable calls
                {data.admin_cost > 0 && <> · {fmt(data.admin_cost)} admin</>}
              </div>
            </div>
          </div>

          <Table title="By feature" rows={data.by_feature} keyField="feature" />
          <Table title="By provider" rows={data.by_provider} keyField="provider" />
          <Table title="By type" rows={data.by_kind} keyField="kind" />
          <Table title="Top users" rows={data.top_users} keyField="username" />

          {/* Reconcile recorded estimate vs provider-billed actual */}
          <div className="pt-2">
            <div className="flex items-center justify-between mb-1.5">
              <h4 className="text-sm font-semibold text-slate-700">Reconcile vs provider billing</h4>
              <button
                onClick={reconcile}
                disabled={reconLoading}
                className="text-xs px-3 py-1.5 rounded-full bg-slate-700 hover:bg-slate-800 disabled:opacity-40 text-white font-medium transition-colors"
              >
                {reconLoading ? "Checking…" : "Check now"}
              </button>
            </div>
            {recon ? (
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <ReconcileRow label="OpenAI (recorded → billed)" info={recon.openai} />
                <ReconcileRow label="Google (recorded → billed)" info={recon.google} />
              </div>
            ) : (
              <p className="text-xs text-slate-400">
                Pulls the OpenAI Cost API and Google billing export to compare against recorded cost.
                Needs OPENAI_ADMIN_KEY / GOOGLE_BILLING_BQ_TABLE configured.
              </p>
            )}
          </div>

          {data.event_count === 0 && (
            <p className="text-xs text-slate-400">
              No usage recorded yet — events appear here as AI/STT/TTS/OCR calls are made.
            </p>
          )}

          <IrabQualityBlock days={days} />
        </>
      ) : null}
    </div>
  );
}
